'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Search, ShoppingCart, User } from 'lucide-react';
import { useAuthStore } from '@/store/useAuthStore';
import { api } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown';
import { useToast } from '@/components/ui/toast';
import { containerClass } from '@/lib/ui';
import type { UserRole } from '@/types';

export function AppHeader() {
  const router = useRouter();
  const { user, logout, setRole, checkAuth } = useAuthStore();
  const { addToast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (user) {
      api.cart.getCart().then(cart => {
        setCartCount(cart?.items.length || 0);
      }).catch(() => {
        // Ignore errors for cart count
        setCartCount(0);
      });
    } else {
      setCartCount(0);
    }
  }, [user]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      router.push(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const handleLogout = async () => {
    await logout();
    router.push('/');
    addToast({ title: 'Гарах', description: 'Амжилттай гарлаа' });
  };

  const handleRoleChange = (role: UserRole) => {
    setRole(role);
    const roleLabels: Record<UserRole, string> = {
      user: 'Хэрэглэгч',
      seller: 'Борлуулагч',
      admin: 'Админ',
    };
    addToast({ title: 'Эрх өөрчлөгдсөн', description: `${roleLabels[role]} эрх рүү шилжлээ` });
    router.refresh();
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-gray-200 bg-white/95 backdrop-blur-md shadow-sm">
      <div className={containerClass}>
        <div className="flex h-16 items-center justify-between gap-4 md:gap-6">
          {/* Left: Logo/Title */}
          <Link href="/" className="flex items-center space-x-2 shrink-0">
            <span className="text-xl md:text-2xl font-bold text-blue-600">NomadShop</span>
          </Link>

          {/* Center: Search Input */}
          <form onSubmit={handleSearch} className="flex-1 max-w-lg mx-2 md:mx-4">
            <div className="relative w-full">
              <Search className="absolute left-2 md:left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <Input
                type="text"
                placeholder="Бараа хайх..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 md:pl-10 pr-3 md:pr-4 h-9 md:h-10 text-sm md:text-base rounded-full border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </form>

          {/* Right: Cart + Profile/Login */}
          <div className="flex items-center gap-2 shrink-0">
            {/* Cart Button with Badge */}
            <Link href="/cart">
              <Button variant="ghost" size="icon" className="relative h-9 w-9 md:h-10 md:w-10 rounded-lg">
                <ShoppingCart className="h-4 w-4 md:h-5 md:w-5 text-gray-600" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-semibold text-white">
                    {cartCount > 9 ? '9+' : cartCount}
                  </span>
                )}
              </Button>
            </Link>

            {/* Profile/Login Button */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger>
                  <Button variant="ghost" size="icon" className="h-9 w-9 md:h-10 md:w-10 rounded-lg">
                    <User className="h-4 w-4 md:h-5 md:w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end">
                  <div className="px-2 py-1.5 text-sm text-gray-500 border-b">
                    {user.email}
                  </div>
                  <div className="px-2 py-1.5 text-xs text-gray-400 border-b">
                    Эрх: <span className="font-medium">{user.role === 'user' ? 'Хэрэглэгч' : user.role === 'seller' ? 'Борлуулагч' : 'Админ'}</span>
                  </div>
                  <div className="px-2 py-1.5 text-xs text-gray-500 border-b">
                    Эрх солих:
                  </div>
                  <DropdownMenuItem onClick={() => handleRoleChange('user')} className="text-sm">
                    Хэрэглэгч
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleRoleChange('seller')} className="text-sm">
                    Борлуулагч
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleRoleChange('admin')} className="text-sm">
                    Админ
                  </DropdownMenuItem>
                  <div className="border-t my-1" />
                  <Link href="/mypage">
                    <DropdownMenuItem>Миний бүртгэл</DropdownMenuItem>
                  </Link>
                  <Link href="/orders">
                    <DropdownMenuItem>Миний захиалга</DropdownMenuItem>
                  </Link>
                  {(user.role === 'seller' || user.role === 'admin') && (
                    <Link href="/seller/products">
                      <DropdownMenuItem>Борлуулагчийн самбар</DropdownMenuItem>
                    </Link>
                  )}
                  {user.role === 'admin' && (
                    <Link href="/admin/dashboard">
                      <DropdownMenuItem>Админий самбар</DropdownMenuItem>
                    </Link>
                  )}
                  <div className="border-t my-1" />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    Гарах
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link href="/login">
                <Button size="sm" className="rounded-lg text-sm md:text-base">
                  Нэвтрэх
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

